/**
 * Topic/Space RAG helpers. Scope embeddings describe the topic or space itself.
 * Child thread and document vectors stay in their existing tables.
 */

export type ScopeKind = "topic" | "space";
export type ScopeBreadth = "retrieval" | "entire";

export type RetrievalModeLabel =
  | "GLOBAL_RETRIEVAL"
  | "TOPIC_RETRIEVAL_CONTEXT"
  | "SPACE_RETRIEVAL_CONTEXT"
  | "FULL_TOPIC_CONTEXT"
  | "FULL_SPACE_CONTEXT";

export interface ScopeCoverage {
  mode: ScopeBreadth;
  complete: boolean;
  resources_total: number;
  resources_included: number;
  chunks_total: number;
  chunks_included: number;
  /** Set only for the five named modes. Omitted when entire-mode context is partial. */
  retrievalMode?: RetrievalModeLabel;
  scope_type?: ScopeKind | "global";
  scope_id?: string | null;
  resources_resolved?: number;
  chunks_retrieved?: number;
  chunks_deduplicated?: number;
  retrieval_ms?: number;
}

export interface ScopeChunkRef {
  sourceType: string;
  sourceId: string;
  chunkIndex: number;
  chunkText: string;
  similarity: number;
}

/** Budget for "use full scope" packing. Not the whole model window. */
export const SCOPE_ENTIRE_MAX_CHARS = 24_000;

export function canonicalTopicText(input: {
  name?: string | null;
  description?: string | null;
}): string {
  return [input.name, input.description]
    .map((part) => (part ?? "").trim())
    .filter((part) => part.length > 0)
    .join("\n");
}

export function canonicalSpaceText(input: {
  name?: string | null;
  description?: string | null;
  goals?: string | string[] | null;
}): string {
  const goals = Array.isArray(input.goals)
    ? input.goals.map((goal) => goal.trim()).filter((goal) => goal.length > 0).join("\n")
    : (input.goals ?? "").trim();
  return [input.name, input.description, goals]
    .map((part) => (part ?? "").trim())
    .filter((part) => part.length > 0)
    .join("\n");
}

/** Stable fingerprint of canonical semantic text. Not a cryptographic commitment. */
export function hashScopeText(text: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x811c9dc5 ^ text.length;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ code, 0x01000193);
    h2 = Math.imul(h2 ^ code, 0x01000193);
  }
  const hex = (n: number) => (n >>> 0).toString(16).padStart(8, "0");
  return `${hex(h1)}${hex(h2)}${hex(text.length)}`;
}

export function canonicalChunkKey(chunk: {
  sourceType: string;
  sourceId: string;
  chunkIndex: number;
}): string {
  return `${chunk.sourceType}:${chunk.sourceId}:${chunk.chunkIndex}`;
}

export function dedupeScopeChunks<T extends { sourceType: string; sourceId: string; chunkIndex: number }>(
  chunks: readonly T[]
): { chunks: T[]; removed: number } {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const chunk of chunks) {
    const key = canonicalChunkKey(chunk);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(chunk);
  }
  return { chunks: out, removed: chunks.length - out.length };
}

export function retrievalModeFor(input: {
  scope: "global" | ScopeKind;
  breadth: ScopeBreadth;
  complete: boolean;
}): RetrievalModeLabel | undefined {
  if (input.scope === "global") return "GLOBAL_RETRIEVAL";
  if (input.breadth === "retrieval" && input.scope === "topic") return "TOPIC_RETRIEVAL_CONTEXT";
  if (input.breadth === "retrieval" && input.scope === "space") return "SPACE_RETRIEVAL_CONTEXT";
  if (input.breadth === "entire" && input.complete && input.scope === "topic") return "FULL_TOPIC_CONTEXT";
  if (input.breadth === "entire" && input.complete && input.scope === "space") return "FULL_SPACE_CONTEXT";
  return undefined;
}

export function partialScopeNotice(scope: ScopeKind, coverage: ScopeCoverage): string | null {
  if (coverage.mode !== "entire" || coverage.complete) return null;
  const noun = scope === "topic" ? "topic" : "space";
  return (
    `The supplied context is partial (${coverage.chunks_included} of ${coverage.chunks_total} indexed chunks ` +
    `from ${coverage.resources_included} of ${coverage.resources_total} resources). ` +
    `Do not claim you read the entire ${noun}.`
  );
}

/**
 * Broad coverage first, then leftover budget by similarity.
 * A large resource cannot consume the window before smaller resources are represented.
 */
export function selectEntireScopeChunks<T extends ScopeChunkRef>(input: {
  chunks: readonly T[];
  chunksTotal: number;
  resourcesTotal: number;
  maxChars: number;
}): {
  selected: T[];
  coverage: Pick<
    ScopeCoverage,
    "mode" | "complete" | "resources_total" | "resources_included" | "chunks_total" | "chunks_included"
  >;
} {
  const { chunks: unique } = dedupeScopeChunks(input.chunks);
  const groups = new Map<string, T[]>();
  for (const chunk of unique) {
    const key = `${chunk.sourceType}:${chunk.sourceId}`;
    const list = groups.get(key) ?? [];
    list.push(chunk);
    groups.set(key, list);
  }
  for (const list of groups.values()) {
    list.sort((a, b) => b.similarity - a.similarity || a.chunkIndex - b.chunkIndex);
  }

  const fullCorpusPresent = input.chunksTotal === unique.length;
  const totalChars = unique.reduce((sum, chunk) => sum + chunk.chunkText.length, 0);
  const resourcesWithChunks = groups.size;

  if (fullCorpusPresent && totalChars <= input.maxChars) {
    return {
      selected: unique,
      coverage: {
        mode: "entire",
        complete: true,
        resources_total: input.resourcesTotal,
        resources_included: resourcesWithChunks,
        chunks_total: input.chunksTotal,
        chunks_included: unique.length,
      },
    };
  }

  const resourceCount = Math.max(groups.size, 1);
  const share = Math.max(1, Math.floor(input.maxChars / resourceCount));
  const selected: T[] = [];
  const leftovers: T[] = [];
  let usedChars = 0;

  for (const list of groups.values()) {
    let resourceChars = 0;
    for (const chunk of list) {
      const len = chunk.chunkText.length;
      const overShare = resourceChars > 0 && resourceChars + len > share;
      const overBudget = usedChars + len > input.maxChars;
      if (overShare || overBudget) {
        leftovers.push(chunk);
        continue;
      }
      selected.push(chunk);
      resourceChars += len;
      usedChars += len;
    }
  }

  leftovers.sort((a, b) => b.similarity - a.similarity || a.chunkIndex - b.chunkIndex);
  for (const chunk of leftovers) {
    const len = chunk.chunkText.length;
    if (usedChars + len > input.maxChars) continue;
    selected.push(chunk);
    usedChars += len;
  }

  const includedResources = new Set(selected.map((chunk) => `${chunk.sourceType}:${chunk.sourceId}`));
  const complete = fullCorpusPresent && selected.length === unique.length && totalChars <= input.maxChars;

  return {
    selected,
    coverage: {
      mode: "entire",
      complete,
      resources_total: input.resourcesTotal,
      resources_included: includedResources.size,
      chunks_total: input.chunksTotal,
      chunks_included: selected.length,
    },
  };
}

export interface ScopeThreadRow {
  id: string;
  userId: string;
  topicIds?: Array<string | null> | null;
  topicId?: string | null;
  linkedSpaceIds?: Array<string | null> | null;
  deleted?: boolean;
}

export interface ScopeDocumentRow {
  id: string;
  userId: string;
  topicId?: string | null;
  topicIds?: Array<string | null> | null;
  spaceId?: string | null;
  spaceIds?: Array<string | null> | null;
}

export interface ScopeTopicRow {
  id: string;
  spaceId: string;
  secondarySpaceIds?: string[];
}

export interface ScopeAccess {
  /** space id -> role. Missing, empty, or "none" means no access. */
  spaceRoles: Record<string, string | null | undefined>;
}

function cleanIds(ids: Array<string | null | undefined> | null | undefined): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of ids ?? []) {
    const id = (raw ?? "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function liveThreadTopicIds(thread: ScopeThreadRow): string[] {
  return cleanIds([...(thread.topicIds ?? []), thread.topicId]);
}

function hasSpaceRole(access: ScopeAccess, spaceId: string): boolean {
  const role = access.spaceRoles[spaceId];
  return typeof role === "string" && role.length > 0 && role !== "none";
}

export function actorCanReadTopic(topic: ScopeTopicRow, access: ScopeAccess): boolean {
  if (hasSpaceRole(access, topic.spaceId)) return true;
  return (topic.secondarySpaceIds ?? []).some((spaceId) => hasSpaceRole(access, spaceId));
}

function threadInTopic(thread: ScopeThreadRow, topicId: string): boolean {
  if (thread.deleted) return false;
  return liveThreadTopicIds(thread).includes(topicId);
}

function documentInTopic(doc: ScopeDocumentRow, topicId: string): boolean {
  return cleanIds([doc.topicId, ...(doc.topicIds ?? [])]).includes(topicId);
}

function documentInSpace(doc: ScopeDocumentRow, spaceId: string): boolean {
  return cleanIds([doc.spaceId, ...(doc.spaceIds ?? [])]).includes(spaceId);
}

export function resolveTopicResourceIds(input: {
  topicId: string;
  topics: ScopeTopicRow[];
  threads: ScopeThreadRow[];
  documents: ScopeDocumentRow[];
  access: ScopeAccess;
}): { topicIds: string[]; threadIds: string[]; documentIds: string[] } {
  const topic = input.topics.find((row) => row.id === input.topicId);
  if (!topic || !actorCanReadTopic(topic, input.access)) {
    return { topicIds: [], threadIds: [], documentIds: [] };
  }
  const threadIds = input.threads.filter((thread) => threadInTopic(thread, topic.id)).map((thread) => thread.id);
  const documentIds = input.documents.filter((doc) => documentInTopic(doc, topic.id)).map((doc) => doc.id);
  return { topicIds: [topic.id], threadIds, documentIds };
}

export function resolveSpaceResourceIds(input: {
  spaceId: string;
  topics: ScopeTopicRow[];
  threads: ScopeThreadRow[];
  documents: ScopeDocumentRow[];
  access: ScopeAccess;
}): { topicIds: string[]; threadIds: string[]; documentIds: string[] } {
  if (!hasSpaceRole(input.access, input.spaceId)) {
    return { topicIds: [], threadIds: [], documentIds: [] };
  }
  const topicIds = input.topics
    .filter(
      (topic) =>
        (topic.spaceId === input.spaceId || (topic.secondarySpaceIds ?? []).includes(input.spaceId)) &&
        actorCanReadTopic(topic, input.access)
    )
    .map((topic) => topic.id);
  const topicSet = new Set(topicIds);
  const threadIds = input.threads
    .filter((thread) => {
      if (thread.deleted) return false;
      const direct = cleanIds(thread.linkedSpaceIds).includes(input.spaceId);
      const viaTopic = liveThreadTopicIds(thread).some((id) => topicSet.has(id));
      return direct || viaTopic;
    })
    .map((thread) => thread.id);
  const documentIds = input.documents
    .filter(
      (doc) =>
        documentInSpace(doc, input.spaceId) ||
        cleanIds([doc.topicId, ...(doc.topicIds ?? [])]).some((id) => topicSet.has(id))
    )
    .map((doc) => doc.id);
  return { topicIds, threadIds, documentIds };
}
